import { useCallback, useState } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { log } from '@/logger/logger';
import type { GenerateParamValue } from '@/api/generate.api';

const logger = log.getLogger('useSavedModelSettings');

/** The settings a user changed on one model, with the fingerprint of the model's settings at that time. */
export type SavedSettings = {
  fingerprint: string;
  values: Readonly<Record<string, GenerateParamValue>>;
};

/** Per model slug. */
export type SavedModelSettings = Readonly<Record<string, SavedSettings>>;

const NO_SETTINGS: SavedModelSettings = {};

const storageKey = (userId: string) => `weave.generate.model-settings.${userId}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSavedSettings = (value: unknown): value is SavedSettings =>
  isRecord(value) && typeof value.fingerprint === 'string' && isRecord(value.values);

/** What was saved. An entry in any other shape reads as nothing saved for that model. */
function toSavedSettings(parsed: unknown): SavedModelSettings {
  if (!isRecord(parsed)) {
    return NO_SETTINGS;
  }
  return Object.fromEntries(
    Object.entries(parsed).filter((entry): entry is [string, SavedSettings] => isSavedSettings(entry[1])),
  );
}

/**
 * The settings each model was left with, kept across reloads for a signed-in user, and only in memory for a
 * signed-out one. Each user has their own copy, so two accounts on one browser don't share settings.
 */
export function useSavedModelSettings(userId: string | undefined) {
  const { getItem, setItem } = useLocalStorage<unknown>(storageKey(userId ?? ''));
  const read = () => {
    if (!userId) {
      return NO_SETTINGS;
    }
    try {
      return toSavedSettings(getItem());
    } catch {
      // Text that isn't JSON reads as nothing saved.
      return NO_SETTINGS;
    }
  };
  const [state, setState] = useState(() => ({ userId, saved: read() }));
  // A sign-in or a switch of account under the same page reads that user's copy.
  if (state.userId !== userId) {
    setState({ userId, saved: read() });
  }

  const saveModelSettings = useCallback(
    (modelSlug: string, settings: SavedSettings) => {
      const saved = { ...state.saved, [modelSlug]: settings };
      if (state.userId) {
        try {
          setItem(saved);
        } catch (error) {
          // A full or blocked storage only loses the settings on the next reload.
          logger.warn('Could not save the generator settings', error);
        }
      }
      setState({ userId: state.userId, saved });
    },
    [setItem, state],
  );

  return { savedSettings: state.saved, saveModelSettings };
}
